import { ChangeDetectionStrategy, Component, effect, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NotesProvider } from '@models/notes-provider.interface';
import { NoteDto } from '@models/note-dto.interface';

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [CommonModule, FormsModule, MatProgressSpinnerModule, MatButtonModule, MatIconModule],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommentsComponent {
  readonly entityId = input.required<number>();
  readonly provider = input.required<NotesProvider>();

  protected notes = signal<NoteDto[]>([]);
  protected isLoading = signal(false);
  protected isSaving = signal(false);
  protected showAdd = signal(false);
  protected newNoteText = signal('');
  protected editingId = signal<number | null>(null);
  protected editText = signal('');

  constructor() {
    effect(() => {
      const id = this.entityId();
      if (id) {
        this.loadNotes(id);
      }
    });
  }

  protected toggleAdd(): void {
    this.showAdd.update(value => !value);
  }

  protected addComment(): void {
    const text = this.newNoteText().trim();
    if (!text || this.isSaving() || this.notes().length > 0) return;

    const id = this.entityId();
    this.isSaving.set(true);
    this.provider().addNote(id, text).subscribe({
      next: () => {
        this.newNoteText.set('');
        this.showAdd.set(false);
        this.isSaving.set(false);
        this.loadNotes(id);
      },
      error: () => this.isSaving.set(false)
    });
  }

  protected startEdit(note: NoteDto): void {
    this.editingId.set(note.id);
    this.editText.set(note.text);
  }

  protected cancelEdit(): void {
    this.editingId.set(null);
    this.editText.set('');
  }

  protected saveEdit(): void {
    const noteId = this.editingId();
    const text = this.editText().trim();
    if (!noteId || !text || this.isSaving()) return;

    const entityId = this.entityId();
    this.isSaving.set(true);
    this.provider().updateNote(entityId, noteId, text).subscribe({
      next: () => {
        this.editingId.set(null);
        this.editText.set('');
        this.isSaving.set(false);
        this.loadNotes(entityId);
      },
      error: () => this.isSaving.set(false)
    });
  }

  protected deleteComment(noteId: number): void {
    if (this.isSaving()) return;

    const entityId = this.entityId();
    this.isSaving.set(true);
    this.provider().deleteNote(entityId, noteId).subscribe({
      next: () => {
        this.cancelEdit();
        this.newNoteText.set('');
        this.showAdd.set(false);
        this.isSaving.set(false);
        this.loadNotes(entityId);
      },
      error: () => this.isSaving.set(false)
    });
  }

  private loadNotes(entityId: number): void {
    this.isLoading.set(true);
    this.provider().getNotes(entityId).subscribe({
      next: (data) => {
        this.notes.set(data);
        if (data.length > 0) {
          this.showAdd.set(false);
          this.newNoteText.set('');
        }
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }
}
